import { useEffect, useRef, useState, type MouseEvent } from 'react';
import './manager-navigation.css';

type NavigationPage = 'sales-manual' | 'sales-monthly';
type MenuName = 'backend' | 'frontline';

const backendItems = [
  { key: 'home', label: '管理首頁', href: 'https://mnt-manager-access.zeabur.app/?openExternalBrowser=1' },
  { key: 'help', label: '使用手冊', href: 'https://mnt-field-report-wrapper.zeabur.app/manual' },
  { key: 'situation', label: '即時回應', href: 'https://mnt-manager-access.zeabur.app/open/situation?openExternalBrowser=1' },
  { key: 'dashboard', label: '商化查詢', href: 'https://mnt-manager-access.zeabur.app/open/dashboard?openExternalBrowser=1' },
  { key: 'admin', label: '任務發布', href: 'https://mnt-manager-access.zeabur.app/open/admin?openExternalBrowser=1' },
  { key: 'models', label: '系統設定', href: 'https://mnt-manager-access.zeabur.app/open/models?openExternalBrowser=1' },
  { key: 'ocrdemo', label: 'OCR DEMO', href: 'https://mnt-ai-demo-ocr.zeabur.app/?openExternalBrowser=1' },
  { key: 'sales-monthly', label: '銷售月報', href: 'https://mntsales.zeabur.app/monthly-report?openExternalBrowser=1' }
] as const;

const frontlineItems = [
  { key: 'field', label: '商化回報', href: 'https://mnt-field-report-wrapper.zeabur.app/?openExternalBrowser=1' },
  { key: 'guide', label: '圖文說明', href: 'https://mnt-field-report-wrapper.zeabur.app/guide' },
  { key: 'execution', label: '執行須知', href: 'https://mnt-field-report-wrapper.zeabur.app/execution-guide' },
  { key: 'troubleshooting', label: '故障排除', href: 'https://mnt-field-report-wrapper.zeabur.app/troubleshooting' },
  { key: 'updates', label: '近期更新', href: 'https://mnt-field-report-wrapper.zeabur.app/updates' },
  { key: 'quiz', label: '行前測驗', href: 'https://mnt-field-report-wrapper.zeabur.app/quiz?openExternalBrowser=1' },
  { key: 'sales', label: '銷售回報', href: 'https://mntsales.zeabur.app/?openExternalBrowser=1' },
  { key: 'sales-manual', label: '操作說明', href: 'https://mntsales.zeabur.app/manual' }
] as const;

export function ManagerNavigation(props: { pageTitle: string; currentPage: NavigationPage }) {
  const [openMenu, setOpenMenu] = useState<MenuName | null>(null);
  const [busyLabel, setBusyLabel] = useState('');
  const [slow, setSlow] = useState(false);
  const [toast, setToast] = useState('');
  const toastTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const closeMenus = (event: globalThis.MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('.mnav-group')) setOpenMenu(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busyLabel) setOpenMenu(null);
    };
    const clearLoading = () => {
      setBusyLabel('');
      setSlow(false);
    };
    document.addEventListener('click', closeMenus);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('pageshow', clearLoading);
    return () => {
      document.removeEventListener('click', closeMenus);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('pageshow', clearLoading);
    };
  }, [busyLabel]);

  useEffect(() => {
    document.documentElement.toggleAttribute('aria-busy', Boolean(busyLabel));
    document.body.classList.toggle('mnav-navigation-busy', Boolean(busyLabel));
    if (!busyLabel) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), 8000);
    return () => window.clearTimeout(timer);
  }, [busyLabel]);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const toggleMenu = (event: MouseEvent<HTMLButtonElement>, name: MenuName) => {
    event.stopPropagation();
    setOpenMenu(current => current === name ? null : name);
  };

  const handleLink = (event: MouseEvent<HTMLAnchorElement>, label: string, newTab: boolean) => {
    setOpenMenu(null);
    if (newTab || event.ctrlKey || event.metaKey || event.shiftKey) {
      window.clearTimeout(toastTimer.current);
      setToast(`已開新分頁：「${label}」`);
      toastTimer.current = window.setTimeout(() => setToast(''), 2600);
      return;
    }
    if (busyLabel) {
      event.preventDefault();
      return;
    }
    setBusyLabel(label);
  };

  const cancelLoading = () => {
    window.stop();
    setBusyLabel('');
    setSlow(false);
  };

  return (
    <>
      <header className="mnav-shell">
        <div className="mnav-inner">
          <div className="mnav-title">{props.pageTitle}</div>
          <nav className="mnav-groups" aria-label="主管與管理後台">
            <div className="mnav-group">
              <button className="mnav-button" type="button" aria-expanded={openMenu === 'backend'} aria-controls="mnav-backend" onClick={event => toggleMenu(event, 'backend')}>後端管理</button>
              <div id="mnav-backend" className="mnav-menu" hidden={openMenu !== 'backend'}>
                {backendItems.map(item => {
                  const current = item.key === props.currentPage;
                  return <a key={item.key} className={`mnav-item${current ? ' is-current' : ''}`} aria-current={current ? 'page' : undefined} href={item.href} onClick={event => handleLink(event, item.label, false)}><span>{item.label}</span>{current && <small>目前頁面</small>}</a>;
                })}
              </div>
            </div>
            <div className="mnav-group">
              <button className="mnav-button" type="button" aria-expanded={openMenu === 'frontline'} aria-controls="mnav-frontline" onClick={event => toggleMenu(event, 'frontline')}>前線人員</button>
              <div id="mnav-frontline" className="mnav-menu" hidden={openMenu !== 'frontline'}>
                {frontlineItems.map(item => {
                  const current = item.key === props.currentPage;
                  return <a key={item.key} className={`mnav-item${current ? ' is-current' : ''}`} aria-current={current ? 'page' : undefined} href={item.href} target="_blank" rel="noopener" onClick={event => handleLink(event, item.label, true)}><span>{item.label}</span><small>{current ? '目前頁面' : '另開分頁'}</small></a>;
                })}
              </div>
            </div>
          </nav>
        </div>
      </header>
      <div className="mnav-loading" hidden={!busyLabel}>
        <div className="mnav-loading-card" role="status" aria-live="polite">
          <span className="mnav-spinner" aria-hidden="true" />
          <strong>正在開啟「{busyLabel || '頁面'}」</strong>
          <span>{slow ? '仍在載入中，您可以繼續等候，或取消後再試一次。' : '頁面資料較多，請稍候，不需重複點選。'}</span>
          <button className="mnav-cancel" type="button" hidden={!slow} onClick={cancelLoading}>取消等待</button>
        </div>
      </div>
      <div className="mnav-toast" role="status" aria-live="polite" hidden={!toast}>{toast}</div>
    </>
  );
}
